import { getEmployeeBadges } from "@/api";
import { pickBadgeForResponsibility } from "@/utils/inspectionBadgeSync";
import { resolveStoredAuthEmployeeId } from "@components/utils/authEmployeeContext";
import { normalizeBadgeLevel } from "@components/utils/normalizeBadgeLevel";

export function isArchitectWillCoachBadge(badge) {
  if (!badge) return false;
  if (normalizeBadgeLevel(badge.level) !== "architect") return false;
  const value = badge.architect_will_coach ?? badge.master_will_coach;
  return value === true || value === 1 || value === "1";
}

export function resolveIsArchitectWillingToCoach(badge) {
  return isArchitectWillCoachBadge(badge);
}

/**
 * Build selectable EmployeeModal levels (Apprentice/Proficient, optional Coach; never Architect when restricted).
 */
export function buildEmployeeModalSelectableLevels({
  restrictLevels = false,
  isArchitectWillingToCoach = false,
  currentBadgeLevel = null,
  allLevels = [],
}) {
  let baseLevels = allLevels;
  if (restrictLevels) {
    baseLevels = allLevels.filter((level) => {
      if (normalizeBadgeLevel(level.fullName) === "architect") return false;
      if (level.fullName === "Coach") return isArchitectWillingToCoach;
      return true;
    });
  }

  if (!currentBadgeLevel) {
    return baseLevels;
  }

  const currentLevel = allLevels.find((level) => level.fullName === currentBadgeLevel);
  if (!currentLevel) {
    return baseLevels;
  }

  return baseLevels.filter((level) => level.id > currentLevel.id);
}

async function findLoggedInUserBadgeAcrossPages(
  accountId,
  employeeId,
  responsibilityId,
  matchContext = {},
  extraQuery = {}
) {
  let offset = 0;
  let continueFetching = true;

  while (continueFetching) {
    const { data } = await getEmployeeBadges(accountId, employeeId, {
      status: "current",
      limit: 20,
      offset,
      ...extraQuery,
    });
    const results = Array.isArray(data?.results) ? data.results : [];
    const badge = pickBadgeForResponsibility(results, responsibilityId, matchContext);
    if (badge) return badge;

    if (!results.length || !data?.next) {
      continueFetching = false;
    } else {
      offset += results.length;
    }
  }

  return null;
}

export async function fetchActingUserArchitectWillCoachForResponsibility(
  responsibilityId,
  { responsibilityCode, accountId } = {}
) {
  const resolvedAccountId = accountId || localStorage.getItem("currentAccountID");
  const employeeId = resolveStoredAuthEmployeeId();
  if (!resolvedAccountId || !employeeId || responsibilityId == null || responsibilityId === "") {
    return false;
  }

  const matchContext = responsibilityCode ? { responsibilityCode } : {};

  try {
    const numericRespId = Number(responsibilityId);
    const canSendRespId = Number.isFinite(numericRespId) && numericRespId > 0;

    let badge = null;
    if (canSendRespId) {
      badge = await findLoggedInUserBadgeAcrossPages(
        resolvedAccountId,
        employeeId,
        responsibilityId,
        matchContext,
        { responsibility_id: numericRespId }
      );
    }
    if (!badge) {
      badge = await findLoggedInUserBadgeAcrossPages(
        resolvedAccountId,
        employeeId,
        responsibilityId,
        matchContext
      );
    }

    return resolveIsArchitectWillingToCoach(badge);
  } catch (error) {
    console.error("Failed to resolve acting user architect_will_coach:", error);
    return false;
  }
}
